import React, { useEffect, useState, useMemo } from "react";
import { DayPicker } from "react-day-picker";
import { startOfDay, isBefore, isSameDay } from "date-fns";
import "react-day-picker/dist/style.css";
import "../../assets/css/Calenders.css";

export default function Calendars({
    isVendor,
    productId,
    bookingDates = [],
    onClose,
    onDone,
}) {
    const today = useMemo(() => startOfDay(new Date()), []);

    const [selectedDates, setSelectedDates] = useState([]);
    const [loading, setLoading] = useState(false);

    /* =====================
       INIT – remove today from preselected
    ===================== */
    useEffect(() => {
        if (!Array.isArray(bookingDates)) return;

        const normalized = bookingDates
            .map((d) => startOfDay(new Date(d)))
            .filter((d) => !isSameDay(d, today));

        setSelectedDates((prev) => {
            if (
                prev.length === normalized.length &&
                prev.every((d, i) => isSameDay(d, normalized[i]))
            ) {
                return prev;
            }
            return normalized;
        });
    }, [bookingDates, today]);

    /* =====================
       HELPERS
    ===================== */
    const isPast = (day) => isBefore(startOfDay(day), today);

    const formatForApi = (date) => {
        const d = new Date(date);
        const pad = (n, z = 2) => String(n).padStart(z, "0");

        return (
            `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
            `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.` +
            `${pad(d.getMilliseconds(), 3)}`
        );
    };

    /* =====================
       DAY CLICK
    ===================== */
    const onDayClick = (day) => {
        if (!isVendor) return;
        if (isPast(day)) return;

        setSelectedDates((prev) => {
            if (prev.length === 0) {
                return [startOfDay(day)];
            }

            if (prev.length === 1) {
                const start = prev[0];
                const end = startOfDay(day);
                return getDateRange(
                    start < end ? start : end,
                    start < end ? end : start
                );
            }

            return [startOfDay(day)];
        });
    };

    const getDateRange = (start, end) => {
        const dates = [];
        let current = startOfDay(start);
        const last = startOfDay(end);

        while (current <= last) {
            dates.push(current);
            current = new Date(current);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    /* =====================
       DONE – API CALL
    ===================== */
    const handleDone = async () => {
        if (loading) return;
        setLoading(true);

        try {
            const token = localStorage.getItem("token"); // ✅ REAL TOKEN
            const payload = {
                productId,
                dates: selectedDates.map(formatForApi),
            };

            const res = await fetch(
                "https://network.rentel.in/products/rented-dates/update",
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!res.ok) throw new Error("API failed");

            onDone({
                isApi: true,
                rentDates: selectedDates,
            });
        } catch (err) {
            console.error("Booking API error:", err);
        } finally {
            setLoading(false);
        }
    };

    /* =====================
       MODIFIERS
    ===================== */
    const modifiers = {
        booked: selectedDates,
        past: isPast,
    };

    const modifiersClassNames = {
        booked: isVendor ? "day-booked-vendor" : "day-booked-user",
        past: "day-past",
    };

    /* ================= UI ================= */
    return (
        <div className="calendar-backdrop">
        
            <div className={`calendar-box ${isVendor ? "vendor" : "user"}`}>
                <button className="close-btnk" onClick={onClose}>
                    ✕
                </button>

                <DayPicker
                    mode="multiple"
                    selected={selectedDates}
                    onDayClick={onDayClick}
                    fromMonth={today}
                    toDate={new Date(today.getFullYear() + 1, 11)}
                    modifiers={modifiers}
                    modifiersClassNames={modifiersClassNames}
                    showOutsideDays
                    fixedWeeks
                />

                {isVendor && (
                    <div className="action-row">
                        <button className="btn cancel" onClick={onClose}>
                            Cancel
                        </button>

                        <button
                            className="btn done"
                            onClick={handleDone}
                            disabled={loading}
                        >
                            {loading ? "Saving..." : "Done"}
                        </button>
                    </div>
                )}

                {/* ✅ LEGEND – FIXED */}
                <div className="legend">
                    <span
                        className="legend-dot"
                        style={{
                            background: isVendor ? "#e7003d" : "#fda0b9",
                        }}
                    />
                    <span>Booked</span>
                </div>
            </div>
        </div>
    );
}
